import { useCallback } from 'react'

const SCRIPT_MARKER_ATTR = 'data-churnkey'
const READY_PROMISE_KEY = '__churnkeyReady'

const buildScriptUrl = (appId) => `https://assets.churnkey.co/js/app.js?appId=${appId}`

/**
 * useChurnkeyLoader - Returns a stable loader that injects the Churnkey
 * client-side script exactly once per session, exposing `window.churnkey`
 * for the cancel flow and the failed-payment / pause walls.
 *
 * Guards against double-load via `window.churnkey.created` (per Churnkey
 * docs) and an existing-script DOM check (defensive for React StrictMode
 * and SPA re-mounts).
 *
 * The returned `loadChurnkey()` is a Promise that resolves when the injected
 * `<script>` fires its `load` event (i.e. `window.churnkey.check` etc. are
 * safe to call). The Promise is memoised on the script element itself, so
 * repeat calls — including from different hook instances after re-mount —
 * return the same Promise.
 *
 * No-op (returns an already-resolved Promise) when `VITE_CHURNKEY_APP_ID`
 * is not set (e.g. Libellula/Striker builds where Churnkey is disabled).
 *
 * @returns {{ loadChurnkey: () => Promise<void> }} Stable loader. Safe to
 *   include in `useCallback`/`useEffect` dependency arrays.
 */
export const useChurnkeyLoader = () => {
    const loadChurnkey = useCallback(() => {
        const appId = import.meta.env.VITE_CHURNKEY_APP_ID
        if (!appId) {
            console.debug('[Churnkey] VITE_CHURNKEY_APP_ID not set, skipping')
            return Promise.resolve()
        }

        const existing = document.querySelector(`script[${SCRIPT_MARKER_ATTR}="1"]`)
        if (existing) {
            return existing[READY_PROMISE_KEY] ?? Promise.resolve()
        }

        window.churnkey = { created: true }
        const script = document.createElement('script')
        script.src = buildScriptUrl(appId)
        script.async = true
        script.setAttribute(SCRIPT_MARKER_ATTR, '1')

        const readyPromise = new Promise((resolve, reject) => {
            script.onload = () => resolve()
            script.onerror = (event) =>
                reject(event instanceof Error ? event : new Error('Churnkey script failed to load'))
        })
        script[READY_PROMISE_KEY] = readyPromise

        document.head.appendChild(script)
        console.debug('[Churnkey] Script injected')
        return readyPromise
    }, [])

    return { loadChurnkey }
}
